export interface VirtualCashAccount {
  accountNumber: string;
  accountName: string;
  currency: Currency;
  totalBalance: number;
  reservedBalance: number;
  /**
   * NET spendable cash — gross ledger cash minus management fees that have
   * accrued but not yet been extracted.
   *
   * This is the SPEND CAP basis. Every client-side guard (purchase amount,
   * withdrawal amount) must gate on this figure: money owed in fees is not the
   * client's to commit. Never bind a spend cap to `grossAvailableBalance`.
   */
  availableBalance: number;
  /**
   * Management fees accrued against this account and not yet extracted.
   * The gap between what the client sees and what the client may spend.
   */
  feesPayableBalance: number;
  /**
   * GROSS ledger cash — `availableBalance + feesPayableBalance`.
   *
   * This is the DISPLAY basis (feature preservoir-ai#117). Clients see what the
   * ledger actually holds; a net display makes the balance appear to erode daily
   * as fees accrue, even though no money has left the account. Display only —
   * see the warning on `availableBalance`.
   */
  grossAvailableBalance: number;
  createdDate: Date;
}

export interface AccountSummary {
  accountNumber: string;
  /** NET spendable cash — the spend-cap basis. See `VirtualCashAccount`. */
  availableBalance: number;
  /** Accrued, unextracted management fees. */
  feesPayableBalance: number;
  /** GROSS ledger cash — the display basis. See `VirtualCashAccount`. */
  grossAvailableBalance: number;
  currency: Currency;
}

export interface Transaction {
  amount: number;
  isDebit: boolean;
  description: string;
  transactionDate: Date;
  isin?: string;
}

export interface PaginatedTransactions {
  items: Transaction[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface WithdrawalAccount {
  id: string;
  accountName: string;
  maskedAccountNumber: string;
  maskedIBAN: string;
  sortCode: string;
  bankAddress: string;
  currency: Currency;
  isVerified: boolean;
}

export interface WithdrawalRequest {
  corporateClientId: string;
  withdrawalBankAccountId: string;
  amount: number;
}

export interface DepositDetails {
  accountName: string;
  accountNumber: string;
  sortCode: string;
  bankAddress: string;
  referenceNumber: string;
  currency: string;
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  // Add other currencies as needed
}
